import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { resetForSite } from '@store/slices/monitor.slice';
import { writeLastSiteId } from '@utils/lastSite';
import {
  selectProject,
  selectSnapshotStatus,
} from '@store/selectors/monitor.selectors';
import { useFeatures } from '@permissions/useFeatures';
import {
  useMonitorSnapshot,
  useMonitorStream,
} from '@components/Pages/MonitorPage/hooks';
import { BuildingViewSvg } from '@components/Pages/MonitorPage/MainView/BuildingViewSvg';
import { BuildingSkeleton } from '@components/BuildingSkeleton/BuildingSkeleton';
import { DashboardSkeleton } from './DashboardSkeleton';
import { DashboardCard } from './Cards/_shared/DashboardCard';
import { ActivityAlertsCard } from './Cards/ActivityAlertsCard';
import { AiAgentCard } from './Cards/AiAgentCard';
import { MasterScheduleCard } from './Cards/MasterScheduleCard';
import { MorningBriefCard } from './Cards/MorningBriefCard';
import { ProjectProgressCard } from './Cards/ProjectProgressCard';
import { DeliveryCard } from './Cards/DeliveryCard';
import { SafetyComplianceCard } from './Cards/SafetyComplianceCard';
import { WeatherImpactCard } from './Cards/WeatherImpactCard';
import { WorkforceStatusCard } from './Cards/WorkforceStatusCard';
import { useDashboardLayout } from './useDashboardLayout';
import { DashboardGrid } from './DashboardGrid';
import { DashboardLayoutControls } from './DashboardLayoutControls';
import { offeredItems, type CardId } from './layout.defaults';
import { PageActions } from '@components/ui/PageFrame/PageFrame';
import styles from './styles.module.css';

/* ---------------------------------------------------------------
   Dashboard / Command Center
   Mirrors the Lovable "Project Command Center" layout. Real-data
   cards (Visual Site Overview, Workforce, Safety Alerts) reuse the
   MonitorPage's components without editing those files; the
   snapshot + SSE stream hooks hydrate the shared Redux state.

   Cards are addressed by stable string ids and placed on a
   draggable/resizable 12-column grid (`DashboardGrid`). The active
   layout — Default or a user-saved one — is resolved by
   `useDashboardLayout`; `DashboardLayoutControls` drives the
   switcher and edit/save flow.
--------------------------------------------------------------- */

// Display-only height overrides for the AI agent card. Hoisted to module
// scope (not inside the component) because they are pure constants and
// declaring them inside the render function was creating new bindings on
// every render tick.
//   AI_AGENT_EXPANDED_H — minimum height once a conversation opens.
//   AI_AGENT_COLLAPSED_H — capped height while the input strip is collapsed.
// These are CONSTANTS, deliberately not `item.minH` — see the inline comment
// in the displayLayout memo for why.
const AI_AGENT_EXPANDED_H = 5;
const AI_AGENT_COLLAPSED_H = 2;

export function DashboardPage() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const dispatch = useAppDispatch();
  const snapshotStatus = useAppSelector(selectSnapshotStatus);
  const project = useAppSelector(selectProject);

  useEffect(() => {
    if (siteId) {
      dispatch(resetForSite(siteId));
      writeLastSiteId(siteId);
    }
  }, [siteId, dispatch]);

  useMonitorSnapshot(siteId ?? null);
  useMonitorStream(siteId ?? null);

  const dash = useDashboardLayout();

  // Cards whose data the role may not read are dropped from the grid and from
  // the Cards menu (their saved slots stay in the layout, untouched).
  const features = useFeatures();
  const offered = useMemo(() => offeredItems(dash.layout, features), [dash.layout, features]);

  // The rendered layout: hidden cards dropped, then display-only height
  // overrides applied. Hiding is filtered in BOTH modes — edit mode has to show
  // the arrangement you are about to save, including the gaps a hidden card
  // leaves — while the height overrides stay out of edit mode so the grip
  // resizes the real saved size.
  //
  // Display-only height overrides, applied to the RENDERED layout — never
  // persisted, and edit mode always shows the true saved sizes.
  //   - the AI agent shrinks to the compact height while collapsed (input +
  //     chips) and grows to at least AI_AGENT_EXPANDED_H once a conversation
  //     opens, so the inline chat gets real room instead of the saved compact
  //     size. The compact cap is a CONSTANT, deliberately not `item.minH` —
  //     minH is the resize clamp, and tying the rendered height to it would
  //     change the card's on-screen size whenever the clamp policy moves. A
  //     user-saved h below the cap wins (Math.min keeps their choice).
  // `aiAgentCollapsed` starts true because that's the card's initial state —
  // starting false would flash an expanded cell before its first report.
  // AI_AGENT_EXPANDED_H / AI_AGENT_COLLAPSED_H are module-scope constants
  // (hoisted above the component) — see their comment there.
  const [aiAgentCollapsed, setAiAgentCollapsed] = useState(true);
  const displayLayout = useMemo(() => {
    const visible = offered.filter((item) => !item.hidden);
    if (dash.editing) return visible;
    return visible.map((item) => {
      if (item.i === 'ai-agent') {
        return aiAgentCollapsed
          ? { ...item, h: Math.min(item.h, AI_AGENT_COLLAPSED_H) }
          : { ...item, h: Math.max(item.h, AI_AGENT_EXPANDED_H) };
      }
      return item;
    });
  }, [offered, dash.editing, aiAgentCollapsed]);

  // Stable card registry — one entry per known CardId. The grid places these
  // by id; positions/sizes live in the resolved layout, not here. Memoized so
  // edit-mode drag ticks (which re-render the page via the draft state) don't
  // rebuild ten card elements per frame.
  const cards: Record<CardId, ReactNode> = useMemo(() => ({
    // No siteId key: the card holds no local state, and its query is keyed by
    // the site already — it re-reads on a switch without being remounted.
    'morning-brief': <MorningBriefCard />,
    'visual-site-overview': (
      <DashboardCard
        title={t('dashboard.visualSiteOverview.title')}
        bodyClassName={styles.buildingBody}
      >
        {/* loadingFallback overrides BuildingViewSvg's default Monitor
            tower with the shared skeleton in `flexFill` mode so the stack
            distributes across this card's grid cell instead of overflowing. */}
        <BuildingViewSvg
          selectedFloorId={null}
          onSelect={() => { /* noop */ }}
          loadingFallback={<BuildingSkeleton flexFill />}
        />
      </DashboardCard>
    ),
    workforce: <WorkforceStatusCard />,
    'project-progress': <ProjectProgressCard />,
    'master-schedule': <MasterScheduleCard />,
    // Keyed on siteId so a site switch remounts it, resetting the card's
    // local UI state (today-only toggle, windowing, enter-animation
    // tracking) instead of carrying it over onto the new site's alerts. The
    // query itself is already siteId-keyed, so this is purely about local
    // state, not the cache.
    activities: <ActivityAlertsCard key={siteId} />,
    safety: <SafetyComplianceCard />,
    delivery: <DeliveryCard />,
    weather: <WeatherImpactCard />,
    // Keyed on siteId so a site switch remounts it, dropping the previous
    // site's in-memory thread and re-running the persisted-conversation
    // restore for the new site (the card holds local chat state, not query).
    'ai-agent': <AiAgentCard key={siteId} onCollapsedChange={setAiAgentCollapsed} />,
  }), [siteId, t]);

  let body: ReactNode;
  if (snapshotStatus === 'error') {
    body = <p className={styles.snapshotError}>{t('dashboard.snapshotFailed')}</p>;
  } else if (!project) {
    body = <DashboardSkeleton />;
  } else if (displayLayout.length === 0) {
    // Every card turned off. The header keeps its controls, so "Edit layout"
    // → Cards is the way back — say so rather than showing a blank canvas.
    body = (
      <div className={styles.allHidden}>
        <p className={styles.allHiddenTitle}>{t('dashboard.layout.allHidden')}</p>
        <p className={styles.allHiddenHint}>{t('dashboard.layout.allHiddenHint')}</p>
      </div>
    );
  } else {
    body = (
      <DashboardGrid
        layout={displayLayout}
        editing={dash.editing}
        onLayoutChange={dash.onDraftChange}
        cards={cards}
        // The ✕ only exists on cards that are currently rendered, so a
        // toggle there can only ever mean "hide".
        onHideCard={dash.toggleCardVisibility}
      />
    );
  }

  return (
    <div className={styles.page}>
      {project && (
        <PageActions>
          <DashboardLayoutControls
            layouts={dash.layouts}
            activeLayoutId={dash.activeLayoutId}
            editing={dash.editing}
            canUpdateActive={dash.canUpdateActive}
            // `offered`, not displayLayout: the Cards menu is the only way to
            // bring a card back, so it needs the hidden ones too.
            layoutItems={offered}
            onToggleCard={dash.toggleCardVisibility}
            onSelect={dash.selectLayout}
            onStartEdit={dash.startEdit}
            onCancel={dash.cancelEdit}
            onSave={dash.saveLayout}
            onRename={dash.renameLayout}
            onDelete={dash.removeLayout}
          />
        </PageActions>
      )}
      {/* Non-blocking layout fetch error notice — the grid always renders
          DEFAULT_LAYOUT regardless; this notice surfaces the degraded state
          without blocking the dashboard. Only shown once the project has
          loaded so the notice doesn't flash during the initial skeleton. */}
      {dash.isError && project && (
        <p className={styles.layoutFetchError} role="status">
          {t('dashboard.layout.fetchError')}
        </p>
      )}
      {body}
    </div>
  );
}
